<template>
  <div>
    <login-panel>
      <div slot="title" v-if="!showPasswordResetSuccessFail && !showConfirmationPage && !hasToken">
        Forgot your password?
      </div>
      <div slot="title" v-if="!showPasswordResetSuccessFail && showConfirmationPage">
        Reset password email sent
      </div>
      <div slot="title" v-if="!showPasswordResetSuccessFail && !showConfirmationPage && hasToken">
        Please enter your new password
      </div>
      <div slot="title" v-if="showPasswordResetSuccessFail">
        {{ passwordResetSuccessOrFailTitle }}
      </div>

      <div slot="body">
        <div v-if="showDetailsErrorAlert" class="alert alert-danger alert-dismissible" role="alert">
          <button type="button" class="close" data-dismiss="alert" aria-label="Close">
            <span aria-hidden="true">&times;</span>
          </button>
          <p>
            <strong>Error: </strong>There seems to be a problem with the details entered, please
            check them and try again. If you continue to have problems resetting your password,
            please contact support.
          </p>
        </div>

        <template v-if="showPasswordResetSuccessFail">
          <div class="col-sm-offset-1 col-sm-10 info-text">
            <p>{{ passwordResetSuccessOrFailMsg }}</p>
          </div>
        </template>
        <template v-else>
          <validation-observer v-if="!showConfirmationPage" class="form-horizontal" ref="forgottenPasswordObserver"
                               role="form" autocomplete="off" v-slot="{ invalid }">
            <template v-if="hasToken">
              <div class="row">
                <div class="col-sm-offset-1 col-sm-10 info-text">
                  <p>
                    Please enter your new password in the fields below, then click the reset
                    password button and your new password will be set for you.
                  </p>
                  <p>
                    <strong>Hint: </strong>Your new password must be at least 8 characters long and
                    contain:
                  </p>
                  <ul>
                    <li>1 lower case letter</li>
                    <li>1 upper case letter</li>
                    <li>1 number</li>
                  </ul>
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label for="password" class="col-sm-3 control-label">Password</label>
                  <validation-provider class="col-sm-9" mode="eager" rules="required|min:8|verify_password|"
                                       tag="div" v-slot="{ errors }" vid="password">
                    <input type="password" class="form-control" :class="{ 'is-invalid': errors[0] }"
                           id="password" name="password" autocomplete="off" required ref="password"
                           placeholder="Password" v-model="password" />
                    <div class="text-danger">{{ errors[0] }}</div>
                  </validation-provider>
                </div>
                <div class="form-group">
                  <label for="confirmPassword" class="col-sm-3 control-label">Confirm password</label>
                  <validation-provider class="col-sm-9" mode="eager" name="password confirmation"
                                       rules="confirmed:password" tag="div" v-slot="{ errors }">
                    <input type="password" class="form-control" :class="{' is-invalid': errors[0] }"
                           id="confirmPassword" name="confirm-password" autocomplete="off" required
                           placeholder="Confirmation" v-model="confirmPassword" />
                    <div class="text-danger">{{ errors[0] }}</div>
                  </validation-provider>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="col-sm-offset-1 col-sm-10 info-text">
                <p>
                  If you've forgotten your password or need to change it, enter your email address
                  below and click the reset password button.
                </p>
                <p>
                  We'll send you an email containing a link to a page where you can easily create a
                  new password.
                </p>
              </div>
              <div class="form-group">
                <label for="inputEmail" class="col-sm-3 control-label"></label>
                <validation-provider class="col-sm-9" mode="eager" rules="required|email" tag="div" v-slot="{ errors }">
                  <input type="email" class="form-control" :class="{ 'is-invalid': errors[0] } " id="inputEmail"
                         name="email-address" autocomplete="off" placeholder="Email address" v-model="email"
                         required > 
                  <div class="text-danger">{{ errors[0] }}</div>
                </validation-provider>
              </div>
            </template>
            <div class="form-group last">
              <div class="col-sm-offset-3 col-sm-9">
                <button
                  class="btn btn-runReport btn-xs" :class="{ disabled: invalid }"
                  v-on:click.prevent="onClickResetPassword"
                  :disabled="!resetPasswordEnabled"
                >
                  <i class="fas fa-sign-in-alt"></i> {{ resetPasswordText }}
                </button>
              </div>
            </div>
          </validation-observer>

          <div v-if="showConfirmationPage" class="col-sm-offset-1 col-sm-10 info-text">
            <p>We've sent you an email with instructions for resetting your password.</p>
            <p>
              If you don't receive this email shortly, please try checking your junk mail folder. If
              the email is not in your junk folder, please contact support for assistance.
            </p>
          </div>
        </template>
      </div>
      <div slot="footer">
        <button class="btn btn-default" v-on:click.prevent="onClickCancel">
          {{ cancelButtonText }}
        </button>
      </div>
    </login-panel>
  </div>
</template>

<script>
import Axios from '@/packages/axios/Axios';
import LoginPanel from '@/components/login/LoginPanel';

export default {
  components: {
    LoginPanel,
  },
  $_veeValidate: {
    validator: 'new',
  },
  computed: {
    hasToken() {
      return this.$route.params.resetPasswordToken !== undefined;
    },
  },
  data() {
    return {
      confirmPassword: '',
      email: '',
      password: '',
      resetPasswordText: 'Reset Password',
      resetPasswordEnabled: true,
      cancelButtonText: 'Cancel',
      showConfirmationPage: false,
      showConfirmPassword: false,
      showPassword: false,
      showPasswordResetSuccessFail: false,
      passwordResetSuccessOrFailTitle: '',
      passwordResetSuccessOrFailMsg: '',
      showDetailsErrorAlert: false,
    };
  },
  methods: {
    clearErrors() {
      this.showDetailsErrorAlert = false;
    },
    onClickCancel() {
      this.$router.push('/');
    },
    onClickResetPassword() {
      // Step 2: User clicked on link in an email and came back to app to chaneg their password
      if (this.hasToken) {
        this.$refs.forgottenPasswordObserver.validate().then((result) => {
          if (result) {
            Axios.post(`v1/users/reset_password/${this.$route.params.resetPasswordToken}`, {
              password: this.password,
              password_confirmation: this.confirmPassword,
            })
              .then(() => {
                this.passwordResetSuccessOrFailTitle = 'Password successfully changed';
                this.passwordResetSuccessOrFailMsg = 'Your password has been succesfully changed and you can now use it to log in to EndCustomer.';
                this.showPasswordResetSuccessFail = true;
                this.cancelButtonText = 'Return to log in screen';
              })
              .catch(() => {
                this.passwordResetSuccessOrFailTitle = 'Password change unsuccessful';
                this.passwordResetSuccessOrFailMsg = 'Sorry, we have not been able to change your password, please try again or contact support.';
                this.showPasswordResetSuccessFail = true;
                this.cancelButtonText = 'Cancel';
              })
              .finally(() => {
                // this.$store.commit("HIDE_PLEASE_WAIT");
              });
          }
        });
      } else {
        // Step 1: User has entered their email address and has requested a password change email
        this.$refs.forgottenPasswordObserver.validate().then((result) => {
          if (result) {
            this.showDetailsErrorAlert = false;
            this.resetPasswordEnabled = false;
            this.resetPasswordText = 'Please wait...';
            // this.$store.commit("SHOW_PLEASE_WAIT");
            Axios.get(`v1/users/${this.email}/reset_password`)
              .then(() => {
                this.showConfirmationPage = true;
              })
              .catch((error) => {
                if (error.response !== undefined) {
                  if (error.response.status === 404) {
                    // alert("The email address (" + this.email +
                    //   ") is unknown");
                    // this.showDetailsErrorAlert = true;
                    this.showConfirmationPage=true;
                    this.resetPasswordEnabled = true;
                    this.resetPasswordText = 'Reset Password';
                  } else {
                    // this.showDetailsErrorAlert = true;
                    this.showConfirmationPage=true;
                    // console.log(error);
                  }
                } else {
                  // this.showDetailsErrorAlert = true;
                  this.showConfirmationPage=true;
                  // console.log(error);
                }
              })
              .finally(() => {
                // this.$store.commit("HIDE_PLEASE_WAIT");
              });
          }
        });
      }
    },
  },
  name: 'password-reset',
};
</script>

<style scoped></style>
